import { PostgrestError } from "@supabase/supabase-js";
import { Notification } from "providers/NotifierProvider";
import { supabase } from "supabase/client";
import { Database } from "supabase/types";
import logSentryError from "utils/logSentryError";

type DbVote = Database["public"]["Tables"]["votes"];
export interface VoteRow extends Omit<DbVote["Row"], "vehicle_details"> {
  vehicle_details: Vehicle | null;
}
export interface VoteInsert extends Omit<DbVote["Insert"], "vehicle_details"> {
  vehicle_details?: Vehicle | null;
}
export interface VoteUpdate extends Omit<DbVote["Update"], "vehicle_details"> {
  vehicle_details?: Vehicle | null;
}

export enum VehicleType {
  BUS = "bus",
  MID_SIZE_CAR = "mid-size-car",
  MOTORCYCLE = "motorcycle",
  VAN = "van",
}

export interface Vehicle {
  availableSeats: number;
  type: VehicleType;
}

export enum VoteType {
  PARTICIPATING = "participating",
  PENDING = "pending",
}

export type VoteByEventId = Omit<VoteRow, "created_at" | "group_id">;

class VoteApi {
  showNotification?: (notification: Notification) => void;

  constructor(showNotification?: (notification: Notification) => void) {
    this.showNotification = showNotification;
  }

  /**
   * Add new vote
   *
   * @param vote
   * @returns
   */
  async addVote(vote: {
    uid: number;
    type: VoteType;
    event_id: number;
    group_id: number;
  }): Promise<{ vote: VoteRow | null; error: PostgrestError | null }> {
    const { data, error } = await supabase.from("votes").insert(vote).select();

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to add vote.",
          severity: "error",
        });
      }

      logSentryError(error, "VOTE-API: addVote");
      return { vote: null, error };
    }

    return { vote: data[0] ?? null, error: null };
  }

  /**
   * Upsert vote
   *
   * @param vote
   * @returns
   */
  async updateVote(voteId: number, vote: VoteUpdate): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase
      .from("votes")
      .update(vote as DbVote["Update"])
      .eq("id", voteId);

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to update vote.",
          severity: "error",
        });
      }

      logSentryError(error, "VOTE-API: updateVote");
      return { error };
    }

    return { error: null };
  }

  /**
   * Returns votes for the provided events ids
   *
   * @param eventsIds
   * @returns
   */
  async getVotesByEventsIds(eventsIds: number[]): Promise<{
    votes: Array<VoteByEventId>;
    error: PostgrestError | null;
  }> {
    const { data, error } = await supabase
      .from("votes")
      .select("id, uid, type, event_id, joined_on, vehicle_details, passengers, updated_at")
      .in("event_id", eventsIds);

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to load votes.",
          severity: "error",
        });
      }

      logSentryError(error, "VOTE-API: getVotesByEventsIds");
      return { votes: [], error };
    }

    return {
      votes: data as unknown as VoteByEventId[],
      error: null,
    };
  }

  /**
   * Reset votes of an event
   * @param eventId
   * @param groupId
   *
   * @returns
   */
  async resetVotes(eventId: number, groupId: number): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase.rpc("reset_votes", {
      p_event_id: eventId,
      p_group_id: groupId,
    });

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to reset votes.",
          severity: "error",
        });
      }

      logSentryError(error, "VOTE-API: resetVotes");
      return { error };
    }

    return { error: null };
  }

  async takeVehicleSeat(
    driverUid: number,
    eventId: number,
    oldDriverUid?: number
  ): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase.rpc("take_vehicle_seat", {
      driver_uid_param: driverUid,
      event_id_param: eventId,
      old_driver_uid_param: oldDriverUid,
    });

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to take a seat.",
          severity: "error",
        });
      }

      logSentryError(error, "VOTE-API: takeVehicleSeat");
      return { error };
    }

    return { error: null };
  }

  async removeOwnVehicleSeat(driverUid: number, eventId: number): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase.rpc("remove_own_vehicle_seat", {
      driver_uid_param: driverUid,
      event_id_param: eventId,
    });

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to remove seat.",
          severity: "error",
        });
      }

      logSentryError(error, "VOTE-API: removeOwnVehicleSeat");
      return { error };
    }

    return { error: null };
  }
}

export default VoteApi;
